import React, { useCallback, useState, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import metadata from './QuoteUnderwritingIssues.metadata.json5';
import styles from './QuoteUnderwritingIssues.module.scss';
import messages from './QuoteUnderwritingIssues.messages';

const BLOCKS_QUOTES = ['BlocksQuote', 'BlocksQuoteRelease'];
const BLOCKS_BIND = 'BlocksBind';
const NON_BLOCKING = 'NonBlocking';

const DisplayUnderwritingIssues = (props) => {
    const {
        underwritingIssues,
        setUnderwritingIssues,
        blockQuote,
        history,
        quoteID,
        filterUWIssuesInCustomOffering
    } = props;
    const [referToUWForm, setReferToUWForm] = useState(false);
    const [underwriterButton, setUnderwriterButton] = useState(true);
    const [withdrawButton, setWithdrawButton] = useState(true);
    const [formData, updateFormData] = useState({});
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const { quoteUnderWritingService } = useDependencies('quoteUnderWritingService');

    const writeValue = useCallback(
        (value, path) => {
            const nextFormData = _.cloneDeep(formData);

            _.set(nextFormData, path, value);
            updateFormData(nextFormData);
        },
        [formData]
    );

    const getUwIssuesByGroup = useMemo(() => {
        const blockingUWIssues = underwritingIssues.filter(
            (issue) => issue.currentBlockingPoint !== NON_BLOCKING
        );

        return _.groupBy(blockingUWIssues, (issue) => issue.offering);
    }, [underwritingIssues]);

    const displayableIssues = useMemo(() => {
        let issues = _.keys(getUwIssuesByGroup);

        if (filterUWIssuesInCustomOffering) {
            issues = issues.filter((val) => val !== 'CUSTOM');
        }

        return issues.sort();
    }, [filterUWIssuesInCustomOffering, getUwIssuesByGroup]);

    const hasRejected = useMemo(
        () =>
            underwritingIssues.some(
                uwIssue => uwIssue.currentBlockingPoint === 'Rejected'
            ),
        [underwritingIssues]
    );

    if (hasRejected) {
        history.push('/knockoutpage', { underwritingIssues });
    }

    const openUWForm = useCallback(() => {
        setReferToUWForm(true);
        setUnderwriterButton(false);
        setWithdrawButton(false);
    }, []);

    const referToUnderwriter = useCallback(() => {
        if (!quoteUnderWritingService) {
            return;
        }

        const { notes } = formData;

        quoteUnderWritingService.referToUnderwriter(quoteID, notes, authHeader).then(() => {
            history.push(`/quotes/${quoteID}/summary`);
        });
    }, [authHeader, quoteID, history, formData, quoteUnderWritingService]);

    const withdrawHandler = useCallback(() => {
        if (!quoteUnderWritingService) {
            return;
        }

        quoteUnderWritingService.withdrawJobByJobNumber(quoteID, authHeader).then(() => {
            history.push(`/quotes/${quoteID}/summary`);
        });
    }, [authHeader, quoteID, history, quoteUnderWritingService]);

    const cancelButton = useCallback(() => {
        setReferToUWForm(false);
        setUnderwriterButton(true);
        setWithdrawButton(true);
        updateFormData({});
    }, []);

    const getBlockingPointDisplayKey = useCallback(
        (blockingPoint) => {
            switch (blockingPoint) {
                case 'BlocksBind':
                    return translator(messages.blocksBind);
                case 'BlocksQuote':
                    return translator(messages.blocksQuote);
                case 'BlocksQuoteRelease':
                    return translator(messages.blocksQuoteRelease);
                case 'NonBlocking':
                    return translator(messages.nonBlocking);
                default:
                    return blockingPoint;
            }
        },
        [translator]
    );

    const approveQuote = useCallback(
        (evt, DTO) => {
            evt.preventDefault();

            if (!quoteUnderWritingService) {
                return;
            }

            quoteUnderWritingService.approveUnderwritingIssue(quoteID, DTO, authHeader).then(() => {
                setUnderwritingIssues();
            });
        },
        [quoteUnderWritingService, quoteID, authHeader, setUnderwritingIssues]
    );

    const generateIssuesData = useCallback(
        (offering) => {
            const getUwIssueByBindOrBlock = !blockQuote
                ? offering.filter((issue) => issue.currentBlockingPoint === BLOCKS_BIND)
                : offering.filter((issue) => BLOCKS_QUOTES.includes(issue.currentBlockingPoint));

            return getUwIssueByBindOrBlock.map((issue) => {
                const currentBlockingPoint = getBlockingPointDisplayKey(issue.currentBlockingPoint);

                return (
                    <tr>
                        <td className={styles.gwIssueDescription}>{issue.description}</td>
                        {issue.canUserApprove ? (
                            <React.Fragment>
                                <td>{currentBlockingPoint}</td>
                                <td>
                                    <a
                                        className={styles.approve}
                                        href
                                        onClick={(evt) => approveQuote(evt, issue)}
                                    >
                                        {translator(messages.approve)}
                                    </a>
                                </td>
                            </React.Fragment>
                        ) : null}
                    </tr>
                );
            });
        },
        [translator, approveQuote, blockQuote, getBlockingPointDisplayKey]
    );

    const underWritingIssuesTable = useCallback(() => {
        const renderByBlockingPoint = displayableIssues;

        return (
            <table className={styles.gwUwIssuesTable}>
                {renderByBlockingPoint.map((offering) => (
                        <React.Fragment>
                            <tr>
                                {!blockQuote ? (
                                    <td className={styles.gwUwIssueOfferingName} colSpan="3">
                                        {offering}
                                    </td>
                                ) : null}
                            </tr>
                            {generateIssuesData(getUwIssuesByGroup[offering])}
                        </React.Fragment>
                    ))}
            </table>
        );
    }, [generateIssuesData, displayableIssues, getUwIssuesByGroup, blockQuote]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            openUWForm,
            referToUnderwriter,
            cancelButton,
            withdrawHandler
        }
    };
    const overrideProps = {
        gwAlert: {
            visible: blockQuote || displayableIssues.length > 0
        },
        gwAlertHeading: {
            content: translator(messages[blockQuote ? 'weCannotShow' : 'followingQuotes'])
        },
        alertAffectedQuotes: {
            visible: !blockQuote,
            content: displayableIssues.join(',')
        },
        referToUWForm: {
            visible: referToUWForm
        },
        underwriterButton: {
            visible: underwriterButton
        },
        cancelButton: {
            visible: cancelButton
        },
        withdrawButton: {
            visible: withdrawButton
        },
        issuesTable: {
            content: underWritingIssuesTable()
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={formData}
            overrideProps={overrideProps}
            onValueChange={writeValue}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
};

DisplayUnderwritingIssues.propTypes = {
    underwritingIssues: PropTypes.arrayOf({}),
    setUnderwritingIssues: PropTypes.func.isRequired,
    blockQuote: PropTypes.bool,
    quoteID: PropTypes.string.isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    filterUWIssuesInCustomOffering: PropTypes.bool
};
DisplayUnderwritingIssues.defaultProps = {
    underwritingIssues: [],
    blockQuote: false,
    filterUWIssuesInCustomOffering: true
};

const QuoteUnderwritingIssues = (props) => {
    const { underwritingIssues, setUnderwritingIssues } = props;
    const hasUwIssues = underwritingIssues.length;
    const getOnOfferings = useMemo(
        () =>
            underwritingIssues.every(uwIssue =>
                NON_BLOCKING.includes(uwIssue.currentBlockingPoint)
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [underwritingIssues, setUnderwritingIssues]
    );

    // render DisplayUnderwritingIssues only when underwritingIssues are available.
    return hasUwIssues && !getOnOfferings ? <DisplayUnderwritingIssues {...props} /> : null;
};

QuoteUnderwritingIssues.propTypes = {
    underwritingIssues: PropTypes.arrayOf({}).isRequired,
    setUnderwritingIssues: PropTypes.func.isRequired,
    quoteID: PropTypes.string.isRequired,
    filterUWIssuesInCustomOffering: PropTypes.bool
};

QuoteUnderwritingIssues.defaultProps = {
    filterUWIssuesInCustomOffering: true
};

export default withRouter(QuoteUnderwritingIssues);
